/**
 * Normalization rules for LLM processor output: remoteness, job type, locations, benefits.
 * Used by validateJobFields after the processor (or any path) fills job records.
 */

import { BENEFITS_EN, BENEFITS_FI, BENEFITS_SV } from '../../lambda/benefitsEnum';

// --- Remoteness (canonical English → locale labels) ---

export const REMOTENESS_EN_TO_FI: Record<'Remote' | 'Hybrid' | 'Onsite', string> = {
  Remote: 'Etätyö',
  Hybrid: 'Hybridityö',
  Onsite: 'Paikkasidonnainen työ',
};
export const REMOTENESS_EN_TO_SV: Record<'Remote' | 'Hybrid' | 'Onsite', string> = {
  Remote: 'Distans',
  Hybrid: 'Hybrid',
  Onsite: 'På plats',
};

const FI_REMOTENESS_TO_EN: Record<string, 'Remote' | 'Hybrid' | 'Onsite'> = {
  Etätyö: 'Remote',
  Hybridityö: 'Hybrid',
  'Paikkasidonnainen työ': 'Onsite',
};
const SV_REMOTENESS_TO_EN: Record<string, 'Remote' | 'Hybrid' | 'Onsite'> = {
  Distans: 'Remote',
  Hybrid: 'Hybrid',
  'På plats': 'Onsite',
};

/** Hybrid / mixed-work indicators (checked before bare “remote”) */
const REMOTENESS_HYBRID_PATTERNS: RegExp[] = [
  /\bhybrid\b/i,
  /hybridityö/i,
  /hybridarbete/i,
  /remote\s+possible/i,
  /remote\s+optional/i,
  /optional\s+remote/i,
  /distans\s+möjlig/i,
  /etätyö\s+mahdol/i,
  /osittainen\s+etä/i,
  /delvis\s+distans/i,
  /partially\s+remote/i,
  /remote\s*\/\s*onsite/i,
  /onsite\s*\/\s*remote/i,
  /office\s*\+\s*remote/i,
  /\bflexible\b/i,
  /\bflex\b(?!\s*pass)/i,
  /flexib(el|la)/i,
  /joustava/i,
  /\bpart[-\s]?time\b/i,
];

const REMOTENESS_REMOTE_PATTERNS: RegExp[] = [
  /fully\s+remote/i,
  /100\s*%\s*remote/i,
  /complete\s+remote/i,
  /remote[-\s]?only/i,
  /remote[-\s]?first/i,
  /work\s+from\s+home/i,
  /\bwfh\b/i,
  /tele(work|commut)/i,
  /fjärrarbete/i,
  /hemarbete/i,
  /distansarbete/i,
  /anywhere\s+in\s+the\s+world/i,
  /worldwide/i,
  /globally\s+distributed/i,
];

const REMOTENESS_ONSITE_PATTERNS: RegExp[] = [
  /\bon[-\s]?site\b/i,
  /\bin[-\s]?person\b/i,
  /office[-\s]?based/i,
  /\bin[-\s]?office\b/i,
  /at\s+the\s+office/i,
  /\bpå\s+plats\b/i,
  /lähityö/i,
  /toimistotyö/i,
  /paikkasidonnainen/i,
  /kontorsarbete/i,
];

function remotenessRawToEnglish(raw: string): 'Remote' | 'Hybrid' | 'Onsite' | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const lower = trimmed.toLowerCase();

  for (const en of ['Remote', 'Hybrid', 'Onsite'] as const) {
    if (en.toLowerCase() === lower) return en;
  }

  for (const [fi, en] of Object.entries(FI_REMOTENESS_TO_EN)) {
    if (fi.toLowerCase() === lower) return en;
  }
  for (const [sv, en] of Object.entries(SV_REMOTENESS_TO_EN)) {
    if (sv.toLowerCase() === lower) return en;
  }

  // Legacy / mistake: English remoteness words inside Finnish/Swedish fields
  if (lower === 'remote' || lower === 'hybrid' || lower === 'onsite') {
    return lower === 'remote' ? 'Remote' : lower === 'hybrid' ? 'Hybrid' : 'Onsite';
  }

  for (const re of REMOTENESS_HYBRID_PATTERNS) {
    if (re.test(trimmed)) return 'Hybrid';
  }
  for (const re of REMOTENESS_REMOTE_PATTERNS) {
    if (re.test(trimmed)) return 'Remote';
  }

  // Finnish / Swedish phrases that still mean “remote work” (work mode), not hybrid
  if (/etätyö\s+suomessa|työ\s+etänä\s+suomessa|etänä\s+suomessa/i.test(trimmed)) return 'Remote';
  if (/distans\s+(i|från)\s+finland|fjärrarbete\s+i\s+finland/i.test(trimmed)) return 'Remote';
  for (const re of REMOTENESS_ONSITE_PATTERNS) {
    if (re.test(trimmed)) return 'Onsite';
  }

  // Last-resort single-token hints
  if (/\bremote\b/i.test(trimmed)) return 'Remote';

  return undefined;
}

export function normalizeRemotenessForLocale(
  value: string | undefined,
  locale: 'en' | 'sv' | 'fi',
): string | undefined {
  if (!value || typeof value !== 'string') return undefined;
  const en = remotenessRawToEnglish(value);
  if (!en) return undefined;
  if (locale === 'en') return en;
  if (locale === 'fi') return REMOTENESS_EN_TO_FI[en];
  return REMOTENESS_EN_TO_SV[en];
}

// --- Job type (canonical English → locale) ---

export const JOB_TYPE_EN_TO_FI: Record<string, string> = {
  'Full-time': 'Kokoaikainen',
  'Part-time': 'Osa-aikainen',
  Temporary: 'Määräaikainen',
  'Summer Job': 'Kesätyö',
  Contract: 'Sopimuspohjainen työ',
  Internship: 'Harjoittelupaikka',
  'Open Application': 'Avoin hakemus',
  Freelance: 'Keikkatyö',
  Apprenticeship: 'Oppisopimus',
};

export const JOB_TYPE_EN_TO_SV: Record<string, string> = {
  'Full-time': 'Heltid',
  'Part-time': 'Deltid',
  Temporary: 'Tillfällig',
  'Summer Job': 'Sommarjobb',
  Contract: 'Kontrakt',
  Internship: 'Praktik',
  'Open Application': 'Öppen ansökan',
  Freelance: 'Frilans',
  Apprenticeship: 'Lärlingsplats',
};

const VALID_JOB_TYPE_EN = new Set(Object.keys(JOB_TYPE_EN_TO_FI));

const FI_JOB_TYPE_TO_EN: Record<string, string> = Object.fromEntries(
  Object.entries(JOB_TYPE_EN_TO_FI).map(([en, fi]) => [fi.toLowerCase(), en]),
);
const SV_JOB_TYPE_TO_EN: Record<string, string> = Object.fromEntries(
  Object.entries(JOB_TYPE_EN_TO_SV).map(([en, sv]) => [sv.toLowerCase(), en]),
);

function jobTypeRawToEnglish(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();

  if (VALID_JOB_TYPE_EN.has(trimmed)) return trimmed;

  for (const en of VALID_JOB_TYPE_EN) {
    if (en.toLowerCase() === lower) return en;
  }

  // Deprecated processor spellings → canonical EN
  if (lower === 'summer job') return 'Summer Job';
  if (lower === 'open application') return 'Open Application';

  const fromFi = FI_JOB_TYPE_TO_EN[lower];
  if (fromFi) return fromFi;
  const fromSv = SV_JOB_TYPE_TO_EN[lower];
  if (fromSv) return fromSv;

  // Keyword bucketing (English + common FI/SV)
  if (
    /\b(permanent|täysipäiväinen|kokoaikainen|heltid)\b/i.test(trimmed) &&
    !/\b(part|deltid|osa[-\s]?aik)/i.test(trimmed)
  ) {
    return 'Full-time';
  }
  if (/\b(part[-\s]?time|deltid|osa[-\s]?aikainen|osaaikainen)\b/i.test(trimmed)) return 'Part-time';
  if (/\b(intern(ship)?|praktik|harjoittelu(paikka)?|traineeship)\b/i.test(trimmed)) return 'Internship';
  if (/\b(apprentice(ship)?|oppisopimus|lärlings(plats|anställning)?|lärlingsutbildning)\b/i.test(trimmed))
    return 'Apprenticeship';
  if (
    /\b(freelance|frilans|keikkatyö|gig\s+work|self[-\s]?employed|independent\s+contractor)\b/i.test(trimmed)
  )
    return 'Freelance';
  if (/\b(temp(orary)?|määräaikainen|tillfällig|fixed[-\s]?term|vikariat|maternity\s+cover)\b/i.test(trimmed))
    return 'Temporary';
  if (/\b(summer\s+job|sommarjobb|kesätyö|seasonal\s+summer)\b/i.test(trimmed)) return 'Summer Job';
  if (/\b(open\s+application|öppen\s+ansökan|avoin\s+hakemus|continuous\s+application)\b/i.test(trimmed))
    return 'Open Application';
  if (/\b(contract(or)?|sopimuspohjainen|kontrakt|consulting\s+agreement|vendor\s+staff)\b/i.test(trimmed))
    return 'Contract';

  return undefined;
}

export function normalizeJobTypeForLocale(
  value: string | undefined,
  locale: 'en' | 'sv' | 'fi',
): string | undefined {
  if (!value || typeof value !== 'string') return undefined;
  const en = jobTypeRawToEnglish(value);
  if (!en) return undefined;
  if (locale === 'en') return en;
  if (locale === 'fi') return JOB_TYPE_EN_TO_FI[en];
  return JOB_TYPE_EN_TO_SV[en];
}

// --- Locations: bare “Remote” / “Distans” / “Etätyö” → global remote scope ---

const LOCATION_GLOBAL_BY_LOCALE: Record<'en' | 'sv' | 'fi', string> = {
  en: 'Remote - Global',
  sv: 'Distans - Globalt',
  fi: 'Etätyö - Globaali',
};

function isStandaloneRemoteLocation(loc: string, locale: 'en' | 'sv' | 'fi'): boolean {
  const t = loc.trim();
  if (!t) return false;
  if (t.includes('-')) return false;
  const lower = t.toLowerCase();
  if (locale === 'en') return lower === 'remote';
  if (locale === 'sv') return lower === 'distans';
  return lower === 'etätyö' || lower === 'etatyö';
}

export function normalizeStandaloneRemoteInLocationArrays(job: {
  location_en?: string[];
  location_sv?: string[];
  location_fi?: string[];
}): void {
  const fields: Array<{ key: 'location_en' | 'location_sv' | 'location_fi'; locale: 'en' | 'sv' | 'fi' }> = [
    { key: 'location_en', locale: 'en' },
    { key: 'location_sv', locale: 'sv' },
    { key: 'location_fi', locale: 'fi' },
  ];
  for (const { key, locale } of fields) {
    const arr = job[key];
    if (!arr || !Array.isArray(arr)) continue;
    job[key] = arr.map((loc) => {
      if (typeof loc !== 'string') return loc;
      return isStandaloneRemoteLocation(loc, locale) ? LOCATION_GLOBAL_BY_LOCALE[locale] : loc;
    });
  }
}

// --- Benefits: only allow Finland-common enum labels ---

function buildBenefitLookup(canonical: string[]): { exact: Set<string>; lowerToCanonical: Map<string, string> } {
  const exact = new Set(canonical);
  const lowerToCanonical = new Map<string, string>();
  for (const label of canonical) {
    lowerToCanonical.set(label.toLowerCase(), label);
  }
  return { exact, lowerToCanonical };
}

const BENEFITS_LOOKUP_EN = buildBenefitLookup(BENEFITS_EN);
const BENEFITS_LOOKUP_SV = buildBenefitLookup(BENEFITS_SV);
const BENEFITS_LOOKUP_FI = buildBenefitLookup(BENEFITS_FI);

/** Map common near-miss phrases to canonical English benefit label (then locale is unchanged per field). */
const BENEFIT_SYNONYM_TO_EN: Record<string, string> = {
  'lunch benefit card': 'Lunch benefit',
  'meal benefit': 'Lunch benefit',
  'sport benefit': 'Sports & culture benefit',
  'sport and culture': 'Sports & culture benefit',
  'gym benefit': 'Sports & culture benefit',
  'healthcare benefit': 'Well-being / healthcare wallet',
  'health insurance': 'Extra health insurance',
  'private health insurance': 'Extra health insurance',
  'bike benefit': 'Company bike',
  'bicycle benefit': 'Company bike',
  'commuter benefit': 'Commuter pass',
  'phone benefit': 'Work phone',
  'mobile phone': 'Work phone',
  'internet benefit': 'Home-internet benefit',
  'home office allowance': 'Remote-work allowance',
  'remote allowance': 'Remote-work allowance',
  'flexible hours': 'Flexible working hours',
  'flexitime': 'Flexible working hours',
  'education budget': 'Training budget',
  'learning budget': 'Training budget',
  'annual bonus': 'Cash bonus',
  'performance bonus': 'Cash bonus',
  'stock options': 'Stock options / RSU',
  'rsu': 'Stock options / RSU',
  'daycare voucher': 'Child-care voucher',
  'childcare support': 'Child-care voucher',
};

function normalizeOneBenefit(
  raw: string,
  lookup: { exact: Set<string>; lowerToCanonical: Map<string, string> },
  locale: 'en' | 'sv' | 'fi',
): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (lookup.exact.has(trimmed)) return trimmed;
  const byLower = lookup.lowerToCanonical.get(trimmed.toLowerCase());
  if (byLower) return byLower;

  if (locale === 'en') {
    const syn = BENEFIT_SYNONYM_TO_EN[trimmed.toLowerCase()];
    if (syn && BENEFITS_LOOKUP_EN.exact.has(syn)) return syn;
  }

  return undefined;
}

export function filterBenefitsToFinlandWhitelist(job: {
  benefits_en?: string[];
  benefits_sv?: string[];
  benefits_fi?: string[];
}): void {
  if (job.benefits_en && Array.isArray(job.benefits_en)) {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of job.benefits_en) {
      if (typeof item !== 'string') continue;
      const c = normalizeOneBenefit(item, BENEFITS_LOOKUP_EN, 'en');
      if (c && !seen.has(c)) {
        seen.add(c);
        out.push(c);
      }
    }
    job.benefits_en = out;
  }
  if (job.benefits_sv && Array.isArray(job.benefits_sv)) {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of job.benefits_sv) {
      if (typeof item !== 'string') continue;
      const c = normalizeOneBenefit(item, BENEFITS_LOOKUP_SV, 'sv');
      if (c && !seen.has(c)) {
        seen.add(c);
        out.push(c);
      }
    }
    job.benefits_sv = out;
  }
  if (job.benefits_fi && Array.isArray(job.benefits_fi)) {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of job.benefits_fi) {
      if (typeof item !== 'string') continue;
      const c = normalizeOneBenefit(item, BENEFITS_LOOKUP_FI, 'fi');
      if (c && !seen.has(c)) {
        seen.add(c);
        out.push(c);
      }
    }
    job.benefits_fi = out;
  }
}
